/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable no-self-assign */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { SelectModule } from "primeng/select";
import { InputTextModule } from "primeng/inputtext";
import { SkeletonModule } from "primeng/skeleton";
import { AccordionModule } from "primeng/accordion";
import {
  QPilotService,
  QPilotAccessToken,
} from "../../../../../core/services/qpilot.service";
import { SessionService } from "../../../../../core/services/session.service";
import { ConfirmationService, MessageService } from "primeng/api";
import { catchError, of, Subject, takeUntil, tap } from "rxjs";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { ScheduledOrderForm } from "../../../interfaces/scheduled-order-form.interface";
import { US_STATES } from "../../../../../core/constants/us-states";
import { COUNTRIES } from "../../../../../core/constants/countries";

/**
 * Shipping selector component for choosing shipping address for scheduled orders
 * Fetches and displays available shipping addresses for the site
 * COMPACT REDESIGN - Radio toggle between customer address and manual entry
 */
@Component({
  selector: "app-shipping-selector",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SelectModule,
    InputTextModule,
    SkeletonModule,
    AccordionModule,
    ConfirmDialogModule,
  ],
  providers: [ConfirmationService],
  templateUrl: "./shipping-selector.component.html",
  styleUrl: "./shipping-selector.component.scss",
})
export class ShippingSelectorComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  @Input() formGroup!: FormGroup<ScheduledOrderForm>;
  @Input() siteId!: number;
  @Input() disabled = false;
  @Input() required = false;
  @Input() accessToken?: QPilotAccessToken; // Optional token for embedded mode
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() shippingAddressesLoaded = new EventEmitter<any[]>();
  @Output() addressChanged = new EventEmitter<void>(); // Emit when address changes to trigger totals refresh

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  shippingAddresses: any[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  shippingAddressOptions: any[] = [];
  isLoading = false;

  // Address source: 'customer' (from site addresses) or 'manual' (enter manually)
  addressSource: "customer" | "manual" = "customer";

  // State options for dropdown (show abbreviation next to name)
  stateOptions = US_STATES.map((state) => ({
    label: `${state.name} (${state.abbreviation})`,
    value: state.abbreviation,
  }));

  // Country options for dropdown (show alpha2 code next to name)
  countryOptions = COUNTRIES.map((country) => ({
    label: `${country.name} (${country.alpha2})`,
    value: country.alpha2,
  }));

  constructor(
    private qpilotService: QPilotService,
    private sessionService: SessionService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
  ) {}

  ngOnInit(): void {
    // Load shipping addresses if siteId is provided
    if (this.siteId) {
      this.loadShippingAddresses();
    }
  }

  /**
   * Load shipping addresses for the site
   */
  loadShippingAddresses(): void {
    if (!this.siteId) {
      return;
    }

    this.isLoading = true;
    const accessToken = this.accessToken ?? this.sessionService.accessToken;

    if (!accessToken) {
      this.messageService.add({
        severity: "error",
        summary: "Authentication Error",
        detail: "Unable to load shipping addresses. Please sign in again.",
      });
      this.isLoading = false;
      return;
    }

    this.qpilotService
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      .getShippingAddressesForSite(this.siteId, accessToken)
      .pipe(
        takeUntil(this.destroy$),
        tap((response) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
          const addresses = response.Items || [];
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          this.shippingAddresses = addresses;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          this.shippingAddressOptions = this.formatShippingAddresses(addresses);
          this.shippingAddressesLoaded.emit(this.shippingAddresses);

          // If no shipping addresses found and shipping is required, show warning
          if (this.shippingAddresses.length === 0 && this.required) {
            this.messageService.add({
              severity: "warn",
              summary: "No Shipping Addresses",
              detail:
                "No shipping addresses found for this site. Please add one in Settings.",
              life: 5000,
            });
          }
        }),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
        catchError((error) => {
          console.error("Error loading shipping addresses:", error);
          this.messageService.add({
            severity: "error",
            summary: "Load Failed",
            detail: "Failed to load shipping addresses. Please try again.",
          });
          return of({ Items: [] });
        }),
      )
      .subscribe({
        complete: () => {
          this.isLoading = false;
        },
      });
  }

  /**
   * Format shipping addresses for dropdown display
   * @param addresses Raw shipping addresses from API
   * @returns Formatted options for dropdown
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatShippingAddresses(addresses: any[]): any[] {
    return addresses.map((address) => ({
      label: this.getShippingAddressLabel(address),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      value: address.Id,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      data: address,
    }));
  }

  /**
   * Get human-readable label for shipping address
   * @param address Shipping address object
   * @returns Formatted label
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getShippingAddressLabel(address: any): string {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const name = address.Name || "";
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const street1 = address.Street1 || "";
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const city = address.City || "";
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const state = address.State || "";

    const addressPart = [street1, city, state].filter(Boolean).join(", ");

    if (name && addressPart) {
      return `${name} - ${addressPart}`;
    }

    return addressPart || name || "Unnamed Address";
  }

  /**
   * Handle shipping address selection
   * Shows confirmation dialog before applying the address (matches old QMC behavior)
   * @param event Dropdown change event
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onShippingAddressChange(event: any): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const selectedId = event.value;

    // Find the full shipping address object
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const selectedAddress = this.shippingAddresses.find(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (a) => a.Id === selectedId,
    );

    if (!selectedAddress) {
      return;
    }

    // Show confirmation dialog (matches old QMC lines 935-961)
    this.confirmationService.confirm({
      header: "Confirm",
      message:
        "Your Scheduled Order will be updated with the selected Shipping Address. " +
        "The current Shipping Address will be replaced. " +
        "Are you sure you want to make this change?",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Yes",
      rejectLabel: "No",
      accept: () => {
        // User confirmed - apply the address
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.formGroup.controls.siteShippingAddressId.setValue(selectedId);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.formGroup.controls.siteShippingAddress.setValue(selectedAddress);

        // Copy individual address fields to order (matches old QMC lines 700-706)
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.updateShippingFields(selectedAddress);

        // Emit addressChanged to trigger totals refresh in parent (matches old QMC line 709)
        this.addressChanged.emit();

        this.messageService.add({
          severity: "success",
          summary: "Address Updated",
          detail: "Shipping address has been updated successfully.",
          life: 3000,
        });
      },
      reject: () => {
        // User cancelled - dropdown reverts automatically via form binding
      },
    });
  }

  /**
   * Update individual shipping fields from selected address
   * @param address Selected shipping address
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateShippingFields(address: any): void {
    // Handle Name field from shipping address (may be combined name)
    // Try to split into FirstName and LastName if it contains a space
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const name = address.Name || "";
    if (name) {
      const nameParts = name.trim().split(/\s+/);
      if (nameParts.length > 1) {
        // First part is FirstName, rest is LastName
        this.formGroup.controls.shippingFirstName.setValue(nameParts[0]);
        this.formGroup.controls.shippingLastName.setValue(
          nameParts.slice(1).join(" "),
        );
      } else {
        // No space found, put entire name in FirstName
        this.formGroup.controls.shippingFirstName.setValue(name);
        this.formGroup.controls.shippingLastName.setValue("");
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    this.formGroup.controls.shippingStreet1.setValue(address.Street1);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    this.formGroup.controls.shippingStreet2.setValue(address.Street2);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    this.formGroup.controls.shippingCity.setValue(address.City);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    this.formGroup.controls.shippingState.setValue(address.State);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    this.formGroup.controls.shippingPostcode.setValue(address.Postcode);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    this.formGroup.controls.shippingCountry.setValue(address.Country);
  }

  /**
   * Clear shipping address
   */
  clearShippingAddress(): void {
    this.formGroup.controls.siteShippingAddressId.setValue(null);
    this.formGroup.controls.siteShippingAddress.setValue(null);
    this.formGroup.controls.shippingFirstName.setValue("");
    this.formGroup.controls.shippingLastName.setValue("");
    this.formGroup.controls.shippingStreet1.setValue("");
    this.formGroup.controls.shippingStreet2.setValue("");
    this.formGroup.controls.shippingCity.setValue("");
    this.formGroup.controls.shippingState.setValue("");
    this.formGroup.controls.shippingPostcode.setValue("");
    this.formGroup.controls.shippingCountry.setValue("");
  }

  /**
   * Check if shipping address is selected
   * @returns True if shipping address is selected
   */
  hasShippingAddress(): boolean {
    return !!this.formGroup.controls.siteShippingAddressId.value;
  }

  /**
   * Get formatted address for display
   * @returns Formatted address string
   */
  getFormattedAddress(): string {
    const siteShippingAddress =
      this.formGroup.controls.siteShippingAddress.value;

    if (!siteShippingAddress) {
      // Fallback to individual fields if address object not available
      const firstName = this.formGroup.controls.shippingFirstName.value;
      const lastName = this.formGroup.controls.shippingLastName.value;
      const street1 = this.formGroup.controls.shippingStreet1.value;
      const street2 = this.formGroup.controls.shippingStreet2.value;
      const city = this.formGroup.controls.shippingCity.value;
      const state = this.formGroup.controls.shippingState.value;
      const postcode = this.formGroup.controls.shippingPostcode.value;
      const country = this.formGroup.controls.shippingCountry.value;

      const parts = [
        [firstName, lastName].filter(Boolean).join(" "),
        street1,
        street2,
        [city, state, postcode].filter(Boolean).join(", "),
        country,
      ].filter(Boolean);

      return parts.join("\n");
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const addr = siteShippingAddress;
    const parts = [
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      addr.Name,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      addr.Street1,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      addr.Street2,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      [addr.City, addr.State, addr.Postcode].filter(Boolean).join(", "),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      addr.Country,
    ].filter(Boolean);

    return parts.join("\n");
  }

  /**
   * Handle manual shipping field changes
   * Called when user types in manual address fields
   */
  onManualFieldChange(): void {
    // Clear site shipping address ID when manually editing
    this.formGroup.controls.siteShippingAddressId.setValue(null);
    this.formGroup.controls.siteShippingAddress.setValue(null);
  }

  /**
   * Set address source (radio toggle)
   * @param source Address source ('customer' or 'manual')
   */
  setAddressSource(source: "customer" | "manual"): void {
    if (this.disabled) return;
    this.addressSource = source;

    // Clear opposite mode's data when switching
    if (source === "manual") {
      this.formGroup.controls.siteShippingAddressId.setValue(null);
      this.formGroup.controls.siteShippingAddress.setValue(null);
    }
  }

  /**
   * Check if selected country is USA
   * Used to determine whether to show state dropdown or text input
   * @returns True if country is US/USA
   */
  isUSACountry(): boolean {
    const country = this.formGroup.controls.shippingCountry.value;
    return country === "US" || country === "USA";
  }

  /**
   * Handle country change - clear state when switching countries
   */
  onCountryChange(): void {
    // Clear state value when country changes (prevents invalid state for non-US countries)
    this.formGroup.controls.shippingState.setValue("");
    this.onManualFieldChange();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
